/**
 * Composable: keyboard shortcuts for the assessment questionnaire.
 * Use in QuestionnaireComponent; returns a cleanup function to call on unmount.
 *
 * Shortcuts:
 * - Enter: next question
 * - Space: previous question
 * - S: toggle summary sidebar (when not in an input/textarea)
 * - On slider questions: 1–5 set value; ArrowLeft / ArrowRight step between options
 *
 * @param {Object} options
 * @param {Function} options.onNext - called for next question
 * @param {Function} options.onPrevious - called for previous question
 * @param {Function} options.onToggleSummary - called to toggle summary panel
 * @param {Function} options.getIsActive - () => boolean, questionnaire is active (not done/expired)
 * @param {Function} options.getCanGoNext - () => boolean
 * @param {Function} options.getCanGoPrevious - () => boolean
 * @param {Function} options.getQuestionType - () => string, e.g. 'SliderQuestion'
 * @param {Object} options.store - Vuex store (for setAnswer on slider keys)
 * @returns {Function} cleanup - call on component unmount
 */
export default function useQuestionnaireHotkeys({
  store,
  onNext,
  onPrevious,
  onToggleSummary,
  getIsActive,
  getCanGoNext,
  getCanGoPrevious,
  getQuestionType,
}) {
  function isTypingInInput() {
    const el = document.activeElement;
    if (!el || !el.tagName) return false;
    const tag = el.tagName.toUpperCase();
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
  }

  function handleKeydown(e) {
    if (!getIsActive()) return;

    const { key } = e;
    const isSlider = getQuestionType() === 'SliderQuestion';

    // Slider: 1–5 = set value; ArrowLeft / ArrowRight = step between options
    if (isSlider && !isTypingInInput()) {
      if (['1', '2', '3', '4', '5'].includes(key)) {
        e.preventDefault();
        store.commit('setAnswer', Number(key));
        return;
      }
      if (key === 'ArrowLeft' || key === 'ArrowRight') {
        e.preventDefault();
        const raw = store.state.questionnaireManagement?.answer;
        const current = typeof raw === 'number' && raw >= 1 && raw <= 5 ? raw : 1;
        const next = key === 'ArrowRight' ? Math.min(5, current + 1) : Math.max(1, current - 1);
        store.commit('setAnswer', next);
        return;
      }
    }

    // Ignore shortcuts when user is typing
    if (isTypingInInput()) return;

    switch (key) {
      case 'Enter':
        e.preventDefault();
        if (getCanGoNext()) onNext();
        break;
      case ' ':
        e.preventDefault();
        if (getCanGoPrevious()) onPrevious();
        break;
      case 's':
      case 'S':
        e.preventDefault();
        onToggleSummary();
        break;
      default:
        break;
    }
  }

  document.addEventListener('keydown', handleKeydown, true);

  return function cleanup() {
    document.removeEventListener('keydown', handleKeydown, true);
  };
}
